<template>
  <div
    v-if="headerNotify.isShow"
    class="header-notify"
  >
    <div class="notify-alert-wrapper" @click="openNotify">
      <a-alert
        :type="headerNotify.type"
        :show-icon="false"
        :class="['notify-alert', 'hand-mouse', `alert-${headerNotify.type || 'info'}`]"
      >
        <template slot="message">
          <div class="notify-alert-content">
            <a-icon type="gift" class="notify-alert-icon" />
            <div class="notify-alert-text">
              <span class="notify-alert-title">{{ headerNotify.title }}</span>
              <div v-if="countdownData" class="notify-alert-countdown">
                <template v-if="countdownData.expired">
                  <span class="countdown-expired">{{ $t('NOTIFY_COUNTDOWN_EXPIRED') }}</span>
                </template>
                <template v-else>
                  <span class="countdown-label">{{ $t('NOTIFY_COUNTDOWN_REMAINING') }}</span>
                  <div class="countdown-numbers">
                    <span v-if="countdownData.days > 0" class="countdown-item">
                      <span class="countdown-value">{{ countdownData.days }}</span>
                      <span class="countdown-unit">{{ $t('NOTIFY_COUNTDOWN_DAY') }}</span>
                    </span>
                    <span v-if="countdownData.days > 0 || countdownData.hours > 0" class="countdown-item">
                      <span class="countdown-value">{{ countdownData.hoursStr }}</span>
                      <span class="countdown-unit">{{ $t('NOTIFY_COUNTDOWN_HOUR') }}</span>
                    </span>
                    <span class="countdown-item">
                      <span class="countdown-value">{{ countdownData.minutesStr }}</span>
                      <span class="countdown-unit">{{ $t('NOTIFY_COUNTDOWN_MINUTE') }}</span>
                    </span>
                    <span class="countdown-item highlight">
                      <span class="countdown-value">{{ countdownData.secondsStr }}</span>
                      <span class="countdown-unit">{{ $t('NOTIFY_COUNTDOWN_SECOND') }}</span>
                    </span>
                  </div>
                </template>
              </div>
            </div>
            <a-icon type="right-circle" class="notify-alert-arrow" />
          </div>
        </template>
      </a-alert>
    </div>
    <a-modal
      :title="headerNotify.title"
      :visible="headerNotify.popupShow"
      :cancel-text="$t('CANCEL_BTN')"
      :ok-text="$t('OPEN_BTN')"
      :width="headerNotify.width"
      :centered="true"
      class="notification-modal"
      @ok="openPopupOk"
      @cancel="openPopupCancel"
    >
      <div class="modal-content-wrapper">
        <popup-banner v-if="headerNotify.theme === 'popup-banner'" />
        <div v-else class="modal-image-container">
          <img :src="headerNotify.image" class="modal-image" alt="Notification image" />
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import PopupBanner from './PopupBanner.vue';

export default {
  name: 'PaymentHeaderNotify',
  components: {
    PopupBanner,
  },
  props: {
    headerNotify: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      countdownData: null,
      countdownInterval: null,
    };
  },
  watch: {
    'headerNotify.endDate': {
      handler() {
        this.stopCountdown();
        this.$nextTick(() => {
          this.startCountdown();
        });
      },
      immediate: true,
    },
  },
  mounted() {
    this.startCountdown();
  },
  beforeDestroy() {
    this.stopCountdown();
  },
  methods: {
    openNotify() {
      this.$emit('open-notify');
    },
    openPopupOk() {
      this.$emit('open-popup-ok');
    },
    openPopupCancel() {
      this.$emit('open-popup-cancel');
    },
    startCountdown() {
      if (!this.headerNotify?.endDate) {
        this.countdownData = null;
        return;
      }

      const updateCountdown = () => {
        const now = new Date();
        const endDate = new Date(this.headerNotify.endDate);

        // eslint-disable-next-line no-restricted-globals
        if (isNaN(endDate.getTime())) {
          this.countdownData = null;
          return;
        }

        const diff = endDate - now;

        if (diff <= 0) {
          this.countdownData = {
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
            daysStr: '00',
            hoursStr: '00',
            minutesStr: '00',
            secondsStr: '00',
            expired: true,
          };
          this.stopCountdown();
          return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        this.countdownData = {
          days,
          hours,
          minutes,
          seconds,
          daysStr: days.toString().padStart(2, '0'),
          hoursStr: hours.toString().padStart(2, '0'),
          minutesStr: minutes.toString().padStart(2, '0'),
          secondsStr: seconds.toString().padStart(2, '0'),
          expired: false,
        };
      };

      updateCountdown();
      this.countdownInterval = setInterval(updateCountdown, 1000);
    },
    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
  },
};
</script>

<style scoped>
.header-notify {
  margin-bottom: var(--spacing-lg);
}

.notify-alert-wrapper {
  cursor: pointer;
}

.notify-alert {
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  transition: all var(--transition-base);
  border: 1px solid var(--border-color);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-primary);
  box-shadow: var(--shadow-sm);
  position: relative;
}

.notify-alert::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--primary-color);
  border-radius: var(--border-radius-lg) 0 0 var(--border-radius-lg);
}

.notify-alert.alert-success::after { background: var(--success-color); }
.notify-alert.alert-warning::after { background: var(--warning-color); }
.notify-alert.alert-error::after { background: var(--error-color); }
.notify-alert.alert-info::after { background: var(--info-color); }

.notify-alert:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
  border-color: var(--border-color-hover);
}

.notify-alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: var(--spacing-md);
}

.notify-alert-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  min-width: 0;
}

.notify-alert-icon {
  font-size: 22px;
  color: var(--primary-color);
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--primary-light);
  border-radius: var(--border-radius-md);
}

.alert-success .notify-alert-icon { color: var(--success-color); background: var(--success-light); }
.alert-warning .notify-alert-icon { color: var(--warning-color); background: var(--warning-light); }
.alert-error .notify-alert-icon { color: var(--error-color); background: var(--error-light); }
.alert-info .notify-alert-icon { color: var(--info-color); background: var(--info-light); }

.notify-alert-title {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
  line-height: var(--line-height-tight);
  color: var(--text-primary);
}

.notify-alert-countdown {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.countdown-label {
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-xs);
  color: var(--text-tertiary);
  white-space: nowrap;
}

.countdown-expired {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-xs);
  color: var(--error-color);
}

.countdown-numbers {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-wrap: wrap;
}

.countdown-item {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  padding: 2px var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  background: var(--bg-tertiary);
  transition: background var(--transition-fast);
}

.countdown-item.highlight {
  background: var(--primary-light);
}

.alert-success .countdown-item.highlight { background: var(--success-light); }
.alert-warning .countdown-item.highlight { background: var(--warning-light); }
.alert-error .countdown-item.highlight { background: var(--error-light); }
.alert-info .countdown-item.highlight { background: var(--info-light); }

.countdown-value {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
  min-width: 18px;
  text-align: center;
  display: inline-block;
}

.countdown-item.highlight .countdown-value {
  color: var(--primary-color);
}

.alert-success .countdown-item.highlight .countdown-value { color: var(--success-color); }
.alert-warning .countdown-item.highlight .countdown-value { color: var(--warning-color); }
.alert-error .countdown-item.highlight .countdown-value { color: var(--error-color); }
.alert-info .countdown-item.highlight .countdown-value { color: var(--info-color); }

.countdown-unit {
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-xs);
  color: var(--text-tertiary);
  text-transform: lowercase;
}

.notify-alert-arrow {
  font-size: 18px;
  color: var(--text-tertiary);
  transition: transform var(--transition-base), color var(--transition-base);
  flex-shrink: 0;
}

.notify-alert:hover .notify-alert-arrow {
  transform: translateX(3px);
  color: var(--primary-color);
}

.notification-modal .ant-modal-header {
  border-bottom: 1px solid var(--border-color);
  padding: var(--spacing-lg) var(--spacing-xl);
}

.notification-modal .ant-modal-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  text-align: center;
}

.notification-modal .ant-modal-body {
  padding: var(--spacing-xl);
}

.notification-modal--bare .ant-modal-content {
  background: transparent;
  box-shadow: none;
}

.notification-modal--bare .ant-modal-body {
  padding: 0;
}

.notification-modal--bare .modal-content-wrapper {
  border-radius: 0;
  overflow: visible;
}

.modal-content-wrapper {
  border-radius: var(--border-radius-md);
  overflow: hidden;
}

.modal-image-container {
  max-height: 70vh;
  overflow-y: auto;
  border-radius: var(--border-radius-md);
  background: var(--bg-secondary);
  text-align: center;
  -webkit-overflow-scrolling: touch;
}

.modal-image {
  width: 100%;
  height: auto;
  display: block;
  max-width: 100%;
  object-fit: contain;
}

.notification-modal .ant-modal-footer {
  border-top: 1px solid var(--border-color);
  padding: var(--spacing-lg) var(--spacing-xl);
  text-align: center;
}

.notification-modal .ant-modal-footer .ant-btn {
  min-width: 120px;
  height: 40px;
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-md);
}

@media (max-width: 768px) {
  .notify-alert {
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .notify-alert-icon {
    width: 36px;
    height: 36px;
    font-size: 18px;
  }

  .notify-alert-title {
    font-size: var(--font-size-sm);
  }

  .notify-alert-countdown {
    gap: var(--spacing-xs);
  }

  .countdown-numbers {
    gap: 3px;
  }

  .countdown-item {
    padding: 1px 4px;
  }

  .countdown-value {
    font-size: var(--font-size-xs);
    min-width: 16px;
  }

  .notify-alert-arrow {
    font-size: 16px;
  }

  .modal-image-container {
    max-height: 60vh;
  }

  .notification-modal .ant-modal-body {
    padding: var(--spacing-lg);
  }

  .notification-modal .ant-modal-footer .ant-btn {
    min-width: 100px;
    height: 36px;
    font-size: var(--font-size-sm);
  }
}

.hand-mouse {
  cursor: pointer;
}
</style>
